/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	faArrowLeft,
	faArrowRight,
	faChevronDown,
	faFolder,
} from "@fortawesome/pro-regular-svg-icons";
import { Autocomplete, TextField } from "@mui/material";

import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Transforms, BaseEditor, Descendant } from "slate";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { Icon, Modal } from "@/components";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import PromptEditor from "@/pages/Private/pages/List/components/PromptEditor";
import { AiApplyType } from "@/enum/list.enum";

import { TextArea } from "@/components/TextArea/TextArea";
import { InputField } from "@/components/InputField/InputField";
import { ButtonSize, ButtonColor } from "@/components/Button/types";
import { useAppSelector } from "@/redux/hooks";

import suggestionsData from "@/utils/suggestions";
import { Button } from "@/components/Button/Button";

import { List } from "@/pages/Private/pages/List/schema/list";

import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { useRunAiEnrichmentMutation } from "@/pages/Private/redux/list/list.api";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import {
	getSuggestions,
	replaceWithRealValues,
} from "@/pages/Private/pages/List/utils/column-utils";
import {
	ColumnTemplate,
	UpdateColumnTemplate,
	UpdateColumnTemplateSchema,
} from "@/pages/Private/pages/List/schema/column-template";
import { useUpdateColumnTemplateMutation } from "@/pages/Private/redux/column/column.api";

export interface UpdateAIColumnProps {
	open: boolean;
	setOpen: (value: boolean) => void;
	column?: ColumnTemplate;
}

export const UpdateAIColumn: FunctionComponent<UpdateAIColumnProps> = ({
	open,
	setOpen,
	column,
}) => {
	const { meta } = useAppSelector(listSelector);

	const [showPromptLibrary, setShowPromptLibrary] = useState<boolean>(false);
	const [editorValue, setEditorValue] = useState<Descendant[]>();
	const [editor, setEditor] = useState<BaseEditor>();
	const [aiResult, setAiResult] = useState<string>();

	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState<boolean>(false);

	const [currentLead, setCurrentLead] = useState<number>(0);

	const [selectedVariable, setSelectedVariable] = useState<
		{ id: string; name: string } | undefined
	>();

	const handlePreviousLead = () => {
		if (currentLead > 0) {
			setCurrentLead(currentLead - 1);
			setAiResult("");
		}
	};

	const handleNextLead = () => {
		if (currentLead < inbox.length - 1) {
			setCurrentLead(currentLead + 1);
			setAiResult("");
		}
	};

	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);

	const [update, { isLoading, error }] = useUpdateColumnTemplateMutation();
	const [runPrompt, { isLoading: promptIsLoading }] = useRunAiEnrichmentMutation();

	const { inbox } = useAppSelector(listSelector);

	const suggestions = useMemo(() => {
		return inbox[currentLead] ? getSuggestions(inbox[currentLead]) : getSuggestions();
	}, [currentLead, inbox]);

	const getSuggestionLabel = (key: string) => {
		return suggestionsData.suggestions[key as keyof typeof suggestionsData.suggestions] as string;
	};

	const [selectedPrompt, setSelectedPrompt] = useState<ColumnTemplate | null>(null);

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<UpdateColumnTemplate>({
		defaultValues: {
			...column,
		},
		resolver: zodResolver(UpdateColumnTemplateSchema),
	});

	const handleRunPrompt = async () => {
		const result = await runPrompt({
			id: inbox[currentLead].id,
			prompt: replaceWithRealValues(editorValue, inbox[currentLead], suggestions),
		}).unwrap();

		setAiResult(result.data);
	};

	useEffect(() => {
		if (column) {
			setEditorValue(JSON.parse(column.config));
			setEditor(JSON.parse(column.config));
		}
	}, [column]);

	const serialize = (nodes: any) => {
		return JSON.stringify(nodes);
	};

	const onSubmit = async (values: UpdateColumnTemplate) => {
		try {
			values.config = editorValue ? serialize(editorValue) : "";

			await update({
				...values,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	return (
		<Modal
			handleClose={() => {
				setOpen(false);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			size="lg"
			submitButtonText={applyToButtonText}
			title="Update AI Column"
		>
			<div className="text-ssm font-medium mb-2">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("name", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("name")}
				/>
			</div>

			<div className="flex flex-col items-start">
				<div className="flex flex-row justify-between w-full">
					<div className="text-ssm font-medium mt-2 mb-1">Prompt editor</div>
				</div>

				<div className="flex relative flex-col w-full border border-gray-300 rounded-xl">
					<div className="w-full min-h-[120px] ">
						{!selectedPrompt && (
							<PromptEditor
								getSuggestionLabel={getSuggestionLabel}
								handleChange={(value) => {
									setEditor(value);
								}}
								handleValueChange={(value) => {
									setEditorValue(value);
								}}
								suggestions={suggestions}
								value={column ? JSON.parse(column.config) : undefined}
							/>
						)}
						{selectedPrompt && (
							<PromptEditor
								getSuggestionLabel={getSuggestionLabel}
								handleChange={(value) => {
									setEditor(value);
								}}
								handleValueChange={(value) => {
									setEditorValue(value);
								}}
								suggestions={suggestions}
								value={selectedPrompt ? JSON.parse(selectedPrompt.config) : undefined}
							/>
						)}
					</div>
					<div className="p-2 w-full flex border-t border-gray-300 justify-between items-center">
						<div className="flex items-center">
							<div className="mr-2 text-sm text-gray-700">Select</div>
							<Autocomplete
								getOptionLabel={(option: { name: string; id: string }) => option.name}
								id="event"
								options={
									suggestions
										?.filter((key) => {
											const label = getSuggestionLabel(key);

											return label && label !== key;
										})
										?.map((key) => ({
											name: getSuggestionLabel(key) as string,
											id: key,
										})) || []
								}
								popupIcon={
									<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />
								}
								renderInput={(params) => {
									return (
										<TextField
											// sx={{
											// 	height: "40px",
											// }}
											{...params}
											placeholder="Variable"
										/>
									);
								}}
								size="small"
								sx={{
									width: "300px",
									"& .MuiFilledInput-root": {
										paddingTop: "4px!important",
										paddingBottom: "4px!important",
										marginBottom: "0px!important",
									},
									"&.MuiFormControl-root": {
										height: "40px",
									},
								}}
								value={selectedVariable}
								onChange={(event, value) => {
									if (value && editor) {
										const mentionNode = {
											type: "mention" as const,
											character: `${value.id}`,
											children: [
												{
													text: ``,
												},
											],
										};

										// Insert the mention node at the current selection
										Transforms.insertNodes(editor as any, mentionNode);
										// Add a space after the mention
										Transforms.insertText(editor as any, " ");

										setSelectedVariable(undefined);
									}
								}}
							/>
							<div className="ml-2 text-sm text-gray-700">
								or enter{" "}
								<span className="bg-inactive-item px-1 border border-gray-200 rounded-md">
									{"{"}
								</span>{" "}
								to insert variable
							</div>
						</div>
					</div>
				</div>
			</div>

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
